import { useTranslation } from "react-i18next";
import { InspectionDisplayStatus, DefectSeverity, DefectStatus } from "@/types";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { severityBadgeClass, getSeverityConfig } from "@/config/severity";

interface StatusBadgeProps {
  status: InspectionDisplayStatus;
}

const statusClassNames: Record<InspectionDisplayStatus, string> = {
  pending: "bg-muted text-muted-foreground",
  in_progress: "bg-info/15 text-info border-info/30",
  completed: "bg-success/15 text-success border-success/30",
  overdue: "bg-destructive/15 text-destructive border-destructive/30",
  cancelled: "bg-muted text-muted-foreground line-through",
};

export function StatusBadge({ status }: StatusBadgeProps) {
  const { t } = useTranslation();
  const className = statusClassNames[status];
  const statusKey = status === "in_progress" ? "inProgress" : status;
  const label = t(`inspections.status.${statusKey}`);

  return (
    <Badge variant="outline" className={cn("font-medium", className)}>
      {label}
    </Badge>
  );
}

interface SeverityBadgeProps {
  severity: DefectSeverity;
}

// F3 (TES-950): gefuellte Container-Chips statt /15-Outline — pixelgleich zu Stitch screen-07:210.
// Mock bg-error-container/text-on-error-container -> real bg-destructive-container/-foreground (#ffdad6/#93000a).
// medium -> tertiary-container (REPAIR-Aequivalent, da kein secondary-fixed-Token);
// low -> secondary (NOMINAL/Safety-Yellow-Aequivalent, da kein primary-fixed-Token).
// Klassen-Mapping zentral im Severity-SoT (@/config/severity, MGM-08).

export function SeverityBadge({ severity }: SeverityBadgeProps) {
  const { t } = useTranslation();
  const className = severityBadgeClass[severity];
  const label = t(getSeverityConfig(severity).labelKey);

  // F3 (TES-950): pixelgleicher flacher Container-Chip (Stitch screen-07:210 —
  // px-3 py-1 font-bold text-label-caps uppercase). Bewusst KEINE generische rounded-full
  // <Badge> (die wuerde rund statt eckig sein). text-label-caps wird als explizite Utilities
  // inline gerendert (= identische Definition src/index.css:231 — 11px/700/uppercase/0.05em),
  // weil tailwind-merge die Custom-Utility text-label-caps faelschlich als Text-Color einstuft
  // und sie sonst gegen text-*-container-foreground verlieren wuerde.
  return (
    <span
      className={cn(
        "inline-flex items-center rounded border px-3 py-1 text-[11px] font-bold uppercase leading-none tracking-[0.05em]",
        className,
      )}
    >
      {label}
    </span>
  );
}

interface DefectStatusBadgeProps {
  status: DefectStatus;
}

const defectStatusClassNames: Record<DefectStatus, string> = {
  open: "bg-destructive/15 text-destructive border-destructive/30",
  in_progress: "bg-info/15 text-info border-info/30",
  resolved: "bg-success/15 text-success border-success/30",
  closed: "bg-muted text-muted-foreground",
  cancelled: "bg-muted text-muted-foreground line-through",
};

export function DefectStatusBadge({ status }: DefectStatusBadgeProps) {
  const { t } = useTranslation();
  const className = defectStatusClassNames[status];
  const label = t(`defects.status.${status === "in_progress" ? "inProgress" : status}`);

  return (
    <Badge variant="outline" className={cn("font-medium", className)}>
      {label}
    </Badge>
  );
}
